import { useState, useEffect, useRef, Component } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, DollarSign, ShoppingBag, UtensilsCrossed, Users, User,
  BarChart3, Package, Settings, LogOut, Menu, X, ChevronDown,
  Bell, Search, HelpCircle, Store, Shield, Smartphone, Key, KeyRound,
  MapPin, Building2, Check, ChevronRight, RefreshCw, RotateCcw,
  Clock, Banknote, Tag, Eye, EyeOff, CreditCard, Wallet, Calendar,
  FileText, Printer, Layers, Cloud, AlertTriangle, Gift, Sparkles, MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import BillingGate from './BillingGate';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '@/lib/utils';
import OrderNotifications from '@/components/backoffice/OrderNotifications';
import BranchSwitcher from '@/components/BranchSwitcher';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { SyncDrawerTrigger } from '@/components/offline';
import { ThemeToggle } from '@/components/ThemeToggle';
// Removed react-google-recaptcha - using reCAPTCHA Enterprise v3 via script tag

// API URL from config
import { API, API_BASE_URL } from '@/config/api';
import { BO_BASE } from '@/utils/getBasePath';

/** Resolve a back-office route. bo() = dashboard, bo('sales') = /sales or /back-office/sales */
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
// Enable reCAPTCHA Enterprise v3 (invisible, score-based)
const ENABLE_RECAPTCHA = !!RECAPTCHA_SITE_KEY;

// Brand logo - centralized in config for production deployment
import { BACKOFFICE_LOGO } from '@/config/brandAssets';

// Navigation items - Grouped structure with collapsible sections
const navGroups = [
  {
    id: 'main',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: bo() }
    ]
  },
  {
    id: 'sales-orders',
    label: 'Sales & Orders',
    icon: DollarSign,
    items: [
      { id: 'sales', label: 'Sales', icon: DollarSign, path: bo('sales') },
      { id: 'transactions', label: 'Transactions', icon: CreditCard, path: bo('transactions') },
      { id: 'orders', label: 'Orders', icon: ShoppingBag, path: bo('orders') },
      { id: 'refunds', label: 'Refunds', icon: RotateCcw, path: bo('refunds') },
      { id: 'reports', label: 'Reports', icon: BarChart3, path: bo('reports') }
    ]
  },
  {
    id: 'menu-inventory',
    label: 'Menu & Inventory',
    icon: UtensilsCrossed,
    items: [
      { id: 'menu', label: 'Menu', icon: UtensilsCrossed, path: bo('menu') },
      { id: 'menu-import', label: 'AI Menu Builder', icon: FileText, path: bo('menu/import') },
      { id: 'inventory', label: 'Inventory', icon: Package, path: bo('inventory') },
      { id: 'cycle-count', label: 'Cycle Counting', icon: RefreshCw, path: bo('inventory/cycle-count') },
      { id: 'discounts', label: 'Discounts', icon: Tag, path: bo('discounts') }
    ]
  },
  {
    id: 'staff',
    label: 'Staff & Labor',
    icon: Users,
    items: [
      { id: 'employees', label: 'Employees', icon: Users, path: bo('employees') },
      { id: 'pos-login-access', label: 'POS Login Access', icon: Smartphone, path: bo('pos-login-access') },
      { id: 'approval-codes', label: 'Approval codes', icon: KeyRound, path: bo('approval-codes') },
      { id: 'timesheet', label: 'Timesheet', icon: Clock, path: bo('timesheet') },
      { id: 'payroll', label: 'Payroll', icon: Banknote, path: bo('payroll') },
      { id: 'labor-schedule', label: 'Shift Schedule', icon: Calendar, path: bo('labor/schedule') },
      { id: 'labor-tips', label: 'Tip Pooling', icon: DollarSign, path: bo('labor/tips') },
      { id: 'labor-payroll', label: 'Payroll Export', icon: Banknote, path: bo('labor/payroll-export') }
    ]
  },
  {
    id: 'guests',
    label: 'Guests',
    icon: Users,
    items: [
      { id: 'tables', label: 'Tables & Reservations', icon: Calendar, path: bo('tables') },
      { id: 'guest-chat', label: 'Guest Chat', icon: MessageCircle, path: bo('guest-chat') },
      { id: 'clients', label: 'Clients', icon: Users, path: bo('clients') }
    ]
  },
  {
    id: 'kitchen',
    label: 'Kitchen',
    icon: UtensilsCrossed,
    items: [
      { id: 'kitchen-expo', label: 'Kitchen Expo', icon: UtensilsCrossed, path: '/expo' }, // the dining Expo screen (PWA)
      { id: 'kitchen-printers', label: 'Kitchen Printers', icon: Printer, path: bo('kitchen/settings/printers') },
      { id: 'kitchen-stations', label: 'Kitchen Stations', icon: MapPin, path: bo('kitchen/settings/stations') },
      { id: 'kitchen-routing', label: 'Print Routing', icon: Package, path: bo('kitchen/settings/routing') },
      { id: 'kitchen-templates', label: 'Ticket Templates', icon: FileText, path: bo('kitchen/settings/templates') },
      { id: 'kitchen-analytics', label: 'Kitchen Analytics', icon: BarChart3, path: bo('kitchen/analytics') },
      { id: 'kitchen-print-jobs', label: 'Print Jobs', icon: Package, path: bo('kitchen/print-jobs') }
    ]
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: CreditCard,
    items: [
      { id: 'payment-setup', label: 'Payment Settings', icon: Settings, path: bo('payment-setup') },
      { id: 'settlement', label: 'Settlement', icon: Shield, path: bo('settlement') },
      { id: 'gift-cards', label: 'Gift Cards', icon: Gift, path: bo('gift-cards') },
      { id: 'campaigns', label: 'Campaigns', icon: Gift, path: bo('campaigns') },
      { id: 'push-campaigns', label: 'Push Campaigns', icon: Bell, path: bo('push-campaigns') }
    ]
  },
  {
    id: 'aster-ai',
    label: 'ChainAI',
    icon: Sparkles,
    items: [
      { id: 'ai-command', label: 'Command Center', icon: Sparkles, path: bo('ai') },
      { id: 'ai-ops', label: 'Operations Monitor', icon: Sparkles, path: bo('ai/ops') }
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Settings,
    items: [
      { id: 'offline-review', label: 'Sync Review', icon: Cloud, path: bo('offline-review') },
      { id: 'pos-reconciliation', label: 'POS Reconciliation', icon: Shield, path: bo('pos-reconciliation') },
      { id: 'incident-audit', label: 'Incident Audit', icon: AlertTriangle, path: bo('incident-audit') },
      { id: 'app-links', label: 'App Links', icon: Layers, path: bo('apps') },
      { id: 'settings', label: 'Settings', icon: Settings, path: bo('settings') }
    ]
  }
];

// Feature Card Component for Login Hero
function FeatureCard({ icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-slate-800/60 bg-slate-950/30 p-4 hover:bg-slate-950/50 transition-colors">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-blue-600/15 border border-blue-500/15 flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="font-semibold text-slate-200 text-sm">{title}</div>
      </div>
      <div className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

// Error Boundary - catches JS crashes in child pages and shows a fallback UI instead of blank page
class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[PageErrorBoundary] Caught error:', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center" data-testid="page-error-boundary">
          <AlertTriangle className="w-12 h-12 text-orange-500 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-[#d6deeb] mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-500 dark:text-[#637777] mb-4 max-w-md">
            This page encountered an error. Try refreshing or navigating to a different section.
          </p>
          <p className="text-xs font-mono text-red-500 dark:text-red-400 mb-4 max-w-lg break-all">
            {this.state.error?.message || 'Unknown error'}
          </p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function BackOfficeLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(); // Must be called before any early returns
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [manager, setManager] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  // two failed autopay charges: the warning pop-up, then the soft lock (BillingGate)
  const [billingWarning, setBillingWarning] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('backoffice_token'));
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  
  // Sidebar nav group expansion state
  const [expandedGroups, setExpandedGroups] = useState(['sales-orders', 'menu-inventory']);
  
  // Multi-location state
  const [restaurants, setRestaurants] = useState([]);
  const [activeRestaurantId, setActiveRestaurantId] = useState(
    localStorage.getItem('backoffice_active_restaurant') || null
  );
  const [showLocationSwitcher, setShowLocationSwitcher] = useState(false);
  
  // OTP State
  const [showOTPScreen, setShowOTPScreen] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  // proof of the password step; every code call carries it (services/merchant_login_mfa)
  const [mfaToken, setMfaToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMethod, setOtpMethod] = useState('sms'); // 'sms' or 'totp'
  const [phoneNumberMasked, setPhoneNumberMasked] = useState('');
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  
  // Password Reset State
  // ?forgot=1 (owner sign-in's "Forgot password?") opens the reset screen
  const [showPasswordReset, setShowPasswordReset] = useState(
    () => new URLSearchParams(window.location.search).get('forgot') === '1');
  const [resetEmail, setResetEmail] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code, 3: new password
  // proof of the texted code for step 3 (services/manager_password_reset)
  const [resetToken, setResetToken] = useState('');
  const [resetOtpCode, setResetOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPasswordField, setShowNewPasswordField] = useState(false);
  const [showConfirmPasswordField, setShowConfirmPasswordField] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  
  // OTP countdown timer
  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  const fetchManagerProfile = useLatestCallback(async (restaurantId = null) => {
    try {
      const url = restaurantId 
        ? `${API}/merchant/profile?restaurant_id=${restaurantId}`
        : `${API}/merchant/profile`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // sign-up finishes with a plan and a billing card (owner, Nov 5 2026)
      if (response.data.billing_setup_required) {
        navigate('/owner/signup/billing', { replace: true });
        return;
      }
      setBillingWarning(response.data.billing_payment_warning || null);
      setManager(response.data.manager);
      setRestaurant(response.data.restaurant);
      
      // Set restaurants list for multi-location
      if (response.data.restaurants) {
        setRestaurants(response.data.restaurants);
      }
      
      // Set active restaurant
      if (response.data.active_restaurant_id) {
        setActiveRestaurantId(response.data.active_restaurant_id);
        localStorage.setItem('backoffice_active_restaurant', response.data.active_restaurant_id);
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      // Token might be invalid
      if (error.response?.status === 401) {
        localStorage.removeItem('backoffice_token');
        localStorage.removeItem('backoffice_active_restaurant');
        setToken(null);
      } else {
        // For other errors (like 500), still allow the user to use the app with default values
        setManager({ id: null, email: '', full_name: 'User', phone: '', restaurant_id: null });
        setRestaurant({ id: null, name: 'My Restaurant', timezone: 'America/New_York' });
        setRestaurants([]);
      }
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (token) {
      fetchManagerProfile();
    } else {
      setLoading(false);
    }
  }, [token, fetchManagerProfile]);
  
  const handleSwitchLocation = async (restaurantId) => {
    setLoading(true);
    setShowLocationSwitcher(false);
    localStorage.setItem('backoffice_active_restaurant', restaurantId);
    setActiveRestaurantId(restaurantId);
    await fetchManagerProfile(restaurantId);
    toast.success(`Switched to ${restaurants.find(r => r.id === restaurantId)?.name}`);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      // Get reCAPTCHA Enterprise v3 token
      let recaptchaToken = null;
      if (ENABLE_RECAPTCHA && window.grecaptcha?.enterprise) {
        try {
          await window.grecaptcha.enterprise.ready(async () => {});
          recaptchaToken = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'LOGIN' });
        } catch (recaptchaError) {
          console.warn('reCAPTCHA error:', recaptchaError);
          // Continue without reCAPTCHA if it fails
        }
      }
      
      const response = await axios.post(`${API}/merchant/login`, {
        ...loginForm,
        recaptcha_token: recaptchaToken
      });
      
      // Check if OTP is required
      if (response.data.requires_otp) {
        setOtpEmail(response.data.email);
        setMfaToken(response.data.mfa_token || '');
        setPhoneNumberMasked(response.data.phone_masked || '');
        setTotpEnabled(response.data.totp_enabled || false);
        setOtpMethod(response.data.totp_enabled ? 'totp' : 'sms');
        setShowOTPScreen(true);
        
        // Auto-send SMS OTP if SMS is the method
        if (!response.data.totp_enabled) {
          await sendSmsOtp(response.data.mfa_token);
        }
        
        toast.info('Please verify your identity with OTP');
      } else {
        // No OTP required - direct login
        const accessToken = response.data.access_token;
        localStorage.setItem('backoffice_token', accessToken);
        setToken(accessToken);
        setManager(response.data.manager);
        setLoginAttempts(0);
        
        // Dispatch event to refresh branch context
        window.dispatchEvent(new Event('backoffice-login'));
        
        toast.success('Welcome to Chain29 Back Office!');
      }
    } catch (error) {
      setLoginAttempts(prev => prev + 1);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoggingIn(false);
    }
  };
  
  const sendSmsOtp = async (token) => {
    setSendingOTP(true);
    try {
      const res = await axios.post(`${API}/otp/send-sms`, { mfa_token: token });
      setOtpCountdown(res.data?.resend_after || 60);
      toast.success('OTP sent to your phone');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOTP(false);
    }
  };
  
  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setVerifyingOTP(true);
    try {
      const endpoint = otpMethod === 'sms' ? '/otp/verify-sms' : '/otp/verify-totp';
      const payload = otpMethod === 'sms' 
        ? { mfa_token: mfaToken, otp_code: otpCode }
        : { mfa_token: mfaToken, totp_code: otpCode };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('backoffice_token', response.data.access_token);
      setToken(response.data.access_token);
      setManager(response.data.manager);
      
      // Handle multi-location data
      if (response.data.restaurants) {
        setRestaurants(response.data.restaurants);
      }
      if (response.data.primary_restaurant_id) {
        setActiveRestaurantId(response.data.primary_restaurant_id);
        localStorage.setItem('backoffice_active_restaurant', response.data.primary_restaurant_id);
      }
      
      // Dispatch event to refresh branch context
      window.dispatchEvent(new Event('backoffice-login'));
      
      setShowOTPScreen(false);
      setOtpCode('');
      toast.success('Welcome to Chain29 Back Office!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP code');
      setOtpCode('');
    } finally {
      setVerifyingOTP(false);
    }
  };
  
  const handleBackToLogin = () => {
    setShowOTPScreen(false);
    setShowPasswordReset(false);
    setOtpCode('');
    setOtpEmail('');
    setMfaToken('');
    setResetStep(1);
    setResetEmail('');
    setResetToken('');
    setResetOtpCode('');
    setNewPassword('');
    setConfirmPassword('');
  };
  
  // Password Reset Handlers
  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setIsResetting(true);
    try {
      // the answer never says whether the email has an account
      const response = await axios.post(`${API}/otp/request-password-reset`, { email: resetEmail });
      setResetStep(2);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsResetting(false);
    }
  };
  
  const handleVerifyResetOTP = async () => {
    if (resetOtpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setIsResetting(true);
    try {
      const response = await axios.post(`${API}/otp/verify-reset-otp`, { 
        email: resetEmail, 
        otp_code: resetOtpCode 
      });
      setResetToken(response.data.reset_token);
      setResetStep(3);
      toast.success('OTP verified! Enter your new password.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setResetOtpCode('');
    } finally {
      setIsResetting(false);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsResetting(true);
    try {
      await axios.post(`${API}/otp/reset-password`, { 
        reset_token: resetToken,
        new_password: newPassword
      });
      toast.success('Password reset successfully! Please log in.');
      handleBackToLogin();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsResetting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('backoffice_token');
    setToken(null);
    setManager(null);
    setRestaurant(null);
    toast.success('Logged out successfully');
  };

  const isActivePath = (path) => {
    const dashboardPath = bo();
    if (path === dashboardPath) {
      return location.pathname === dashboardPath || location.pathname === (BO_BASE || '/');
    }
    return location.pathname.startsWith(path);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <img src={BACKOFFICE_LOGO} alt="Chain29" className="h-20 w-20 animate-pulse" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Login form
  if (!token) {
    // OTP Verification Screen - Premium Dark Theme
    if (showOTPScreen) {
      return (
        <div 
          className="min-h-screen w-full bg-slate-950 flex items-center justify-center p-4"
          data-testid="backoffice-otp-verification"
        >
          {/* Background Effects */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950 to-slate-900" />
            <div
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.15) 1px, transparent 1px)",
                backgroundSize: "52px 52px",
              }}
            />
            <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl" />
          </div>
          
          <div className="relative w-full max-w-md">
            <Card className="border-slate-800/60 bg-slate-900/50 text-slate-100 shadow-[0_30px_80px_rgba(0,0,0,0.55)] backdrop-blur rounded-3xl">
              <CardHeader className="text-center pb-4">
                <div className="h-16 w-16 bg-blue-600/20 border border-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {otpMethod === 'sms' ? (
                    <Smartphone className="h-8 w-8 text-blue-400" />
                  ) : (
                    <Key className="h-8 w-8 text-blue-400" />
                  )}
                </div>
                <CardTitle className="text-2xl font-bold text-white">Two-Factor Authentication</CardTitle>
                <CardDescription className="text-slate-400 mt-2">
                  {otpMethod === 'sms' 
                    ? `Enter the code sent to ${phoneNumberMasked}`
                    : 'Enter the code from Google Authenticator'
                  }
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* OTP Method Toggle (if both available) */}
                {totpEnabled && (
                  <div className="flex justify-center gap-2">
                    <Button
                      variant={otpMethod === 'sms' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setOtpMethod('sms')}
                      className={cn(
                        "flex items-center gap-2",
                        otpMethod === 'sms' 
                          ? "bg-blue-600 hover:bg-blue-500 text-white" 
                          : "border-slate-700 text-slate-300 hover:bg-slate-800"
                      )}
                    >
                      <Smartphone className="h-4 w-4" />
                      SMS
                    </Button>
                    <Button
                      variant={otpMethod === 'totp' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setOtpMethod('totp')}
                      className={cn(
                        "flex items-center gap-2",
                        otpMethod === 'totp' 
                          ? "bg-blue-600 hover:bg-blue-500 text-white" 
                          : "border-slate-700 text-slate-300 hover:bg-slate-800"
                      )}
                    >
                      <Key className="h-4 w-4" />
                      Authenticator
                    </Button>
                  </div>
                )}
                
                {/* OTP Input */}
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={setOtpCode}
                    onComplete={handleVerifyOTP}
                    autoFocus
                    data-testid="otp-input"
                  >
                    <InputOTPGroup className="gap-2">
                      <InputOTPSlot index={0} className="w-12 h-14 text-xl font-semibold bg-slate-950/50 border-slate-700 text-white" />
                      <InputOTPSlot index={1} className="w-12 h-14 text-xl font-semibold bg-slate-950/50 border-slate-700 text-white" />
                      <InputOTPSlot index={2} className="w-12 h-14 text-xl font-semibold bg-slate-950/50 border-slate-700 text-white" />
                      <InputOTPSlot index={3} className="w-12 h-14 text-xl font-semibold bg-slate-950/50 border-slate-700 text-white" />
                      <InputOTPSlot index={4} className="w-12 h-14 text-xl font-semibold bg-slate-950/50 border-slate-700 text-white" />
                      <InputOTPSlot index={5} className="w-12 h-14 text-xl font-semibold bg-slate-950/50 border-slate-700 text-white" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                
                {/* Resend OTP (for SMS) */}
                {otpMethod === 'sms' && (
                  <div className="text-center">
                    {otpCountdown > 0 ? (
                      <p className="text-sm text-slate-500">
                        Resend code in {otpCountdown}s
                      </p>
                    ) : (
                      <Button
                        variant="link"
                        onClick={() => sendSmsOtp(mfaToken)}
                        disabled={sendingOTP}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        {sendingOTP ? 'Sending...' : 'Resend Code'}
                      </Button>
                    )}
                  </div>
                )}
                
                <Button 
                  onClick={handleVerifyOTP}
                  disabled={verifyingOTP || otpCode.length !== 6}
                  className="w-full h-12 text-base font-semibold rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-[0_12px_30px_rgba(37,99,235,0.35)]"
                  data-testid="verify-otp-btn"
                >
                  {verifyingOTP ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    'Verify & Sign In'
                  )}
                </Button>
                
                <Button
                  variant="ghost"
                  onClick={handleBackToLogin}
                  className="w-full text-slate-400 hover:text-white hover:bg-slate-800"
                >
                  Back to Login
                </Button>
              </CardContent>
            </Card>
            
            <div className="flex items-center justify-center gap-2 text-xs text-slate-500 mt-6">
              <Shield className="h-3 w-3" />
              <span>Secured with Two-Factor Authentication</span>
            </div>
          </div>
        </div>
      );
    }
    
    // Password Reset Screen - Premium Dark Theme
    if (showPasswordReset) {
      return (
        <div className="min-h-screen w-full bg-slate-950 flex items-center justify-center p-4" data-testid="password-reset">
          {/* Background Effects */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950 to-slate-900" />
            <div
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.15) 1px, transparent 1px)",
                backgroundSize: "52px 52px",
              }}
            />
            <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl" />
          </div>
          
          <div className="relative w-full max-w-md">
            <Card className="border-slate-800/60 bg-slate-900/50 text-slate-100 shadow-[0_30px_80px_rgba(0,0,0,0.55)] backdrop-blur rounded-3xl">
              <CardHeader className="text-center pb-4">
                <div className="h-16 w-16 bg-blue-600/20 border border-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Key className="h-8 w-8 text-blue-400" />
                </div>
                <CardTitle className="text-2xl font-bold text-white">Reset Password</CardTitle>
                <CardDescription className="text-slate-400 mt-2">
                  {resetStep === 1 && "Enter your account email. We'll text a code to the phone on file."}
                  {resetStep === 2 && "Enter the code we texted to the phone on file"}
                  {resetStep === 3 && "Create your new password"}
                </CardDescription>
                
                {/* Step Indicator */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={cn(
                        "h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                        resetStep >= step 
                          ? "bg-blue-600 text-white" 
                          : "bg-slate-800 text-slate-500"
                      )}>
                        {resetStep > step ? <Check className="h-4 w-4" /> : step}
                      </div>
                      {step < 3 && (
                        <div className={cn(
                          "w-8 h-0.5 mx-1",
                          resetStep > step ? "bg-blue-600" : "bg-slate-800"
                        )} />
                      )}
                    </div>
                  ))}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Step 1: Email */}
                {resetStep === 1 && (
                  <form onSubmit={handleRequestPasswordReset} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Email</label>
                      <Input
                        type="email"
                        placeholder="you@restaurant.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="h-12 bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500"
                        required
                        data-testid="reset-email-input"
                      />
                      <p className="text-xs text-slate-500">
                        The email you sign in with
                      </p>
                    </div>
                    <Button 
                      type="submit" 
                      disabled={isResetting}
                      className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-[0_12px_30px_rgba(37,99,235,0.35)]"
                    >
                      {isResetting ? 'Sending...' : 'Send Verification Code'}
                    </Button>
                  </form>
                )}

                {/* Step 2: OTP Verification */}
                {resetStep === 2 && (
                  <form onSubmit={(e) => { e.preventDefault(); handleVerifyResetOTP(); }} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Verification Code</label>
                      <Input
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={resetOtpCode}
                        onChange={(e) => setResetOtpCode(e.target.value)}
                        className="h-12 bg-slate-950/50 border-slate-700 text-white text-center text-xl tracking-widest placeholder:text-slate-500"
                        maxLength={6}
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={isResetting}
                      className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-[0_12px_30px_rgba(37,99,235,0.35)]"
                    >
                      {isResetting ? 'Verifying...' : 'Verify Code'}
                    </Button>
                  </form>
                )}

                {/* Step 3: New Password */}
                {resetStep === 3 && (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">New Password</label>
                      <div className="relative">
                        <Input
                          type={showNewPasswordField ? "text" : "password"}
                          placeholder="••••••••••"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="h-12 bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPasswordField(!showNewPasswordField)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                        >
                          {showNewPasswordField ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Confirm Password</label>
                      <div className="relative">
                        <Input
                          type={showConfirmPasswordField ? "text" : "password"}
                          placeholder="••••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="h-12 bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPasswordField(!showConfirmPasswordField)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                        >
                          {showConfirmPasswordField ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      disabled={isResetting}
                      className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-[0_12px_30px_rgba(37,99,235,0.35)]"
                    >
                      {isResetting ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </form>
                )}

                <Button
                  variant="ghost"
                  onClick={handleBackToLogin}
                  className="w-full text-slate-400 hover:text-white hover:bg-slate-800"
                >
                  Back to Login
                </Button>
              </CardContent>
            </Card>
            
            <div className="flex items-center justify-center gap-2 text-xs text-slate-500 mt-6">
              <Shield className="h-3 w-3" />
              <span>Secure password reset process</span>
            </div>
          </div>
        </div>
      );
    }
    
    // Login Screen - dark command center theme
    return (
      <div className="min-h-screen w-full bg-slate-950 text-slate-100" data-testid="backoffice-login">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950 to-slate-900" />
          {/* Subtle grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.15) 1px, transparent 1px)",
              backgroundSize: "52px 52px",
            }}
          />
          {/* Blue glow effects */}
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
          <div className="absolute -bottom-28 -right-24 h-[28rem] w-[28rem] rounded-full bg-blue-600/10 blur-3xl" />
        </div>

        <div className="relative mx-auto flex min-h-screen w-full max-w-7xl items-stretch px-4 py-6 lg:py-10">
          <div className="grid w-full grid-cols-1 gap-6 lg:gap-8 lg:grid-cols-12">
            
            {/* LEFT: Operations Hero */}
            <div className="hidden lg:flex lg:col-span-7 flex-col justify-between rounded-3xl border border-slate-800/60 bg-slate-900/30 p-8 lg:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.55)] backdrop-blur">
              <div>
                {/* Brand */}
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-600/20 border border-blue-500/20 flex items-center justify-center">
                    <Store className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="text-sm font-semibold tracking-wide text-slate-200">
                    ASTERPOS <span className="text-blue-400">RESTAURANT</span>
                  </div>
                </div>

                {/* Hero Text */}
                <h1 className="mt-8 text-4xl font-extrabold leading-tight tracking-tight text-white md:text-5xl">
                  Run Your Restaurant
                  <span className="block text-slate-300">Like a System.</span>
                </h1>

                <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-400">
                  Real-time sales, staff control, inventory management, menus, and reports — built for speed, accuracy, and operational excellence.
                </p>

                {/* Feature Cards */}
                <div className="mt-10 grid gap-4 md:grid-cols-2">
                  <FeatureCard 
                    icon={<BarChart3 className="h-5 w-5 text-blue-400" />} 
                    title="Live Sales & Analytics" 
                    desc="Track revenue, orders, and performance in real time." 
                  />
                  <FeatureCard 
                    icon={<Users className="h-5 w-5 text-blue-400" />} 
                    title="Staff & Payroll" 
                    desc="Time clock, timesheets, and payroll management." 
                  />
                  <FeatureCard 
                    icon={<Package className="h-5 w-5 text-blue-400" />} 
                    title="Inventory & Menu" 
                    desc="Stock control, menu management, and pricing." 
                  />
                  <FeatureCard 
                    icon={<Shield className="h-5 w-5 text-blue-400" />} 
                    title="Secure Operations" 
                    desc="2FA authentication with audit-ready workflows." 
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="mt-10 flex flex-col gap-2 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span>Secure login • Role-based access • PCI-aware environment</span>
                </div>
                <span>© {new Date().getFullYear()} Chain29 Restaurant Back Office</span>
              </div>
            </div>

            {/* RIGHT: Login Card */}
            <div className="lg:col-span-5 flex items-center justify-center">
              <div className="w-full max-w-md">
                {/* Mobile Brand Header */}
                <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
                  <div className="h-10 w-10 rounded-xl bg-blue-600/20 border border-blue-500/20 flex items-center justify-center">
                    <Store className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="text-lg font-semibold tracking-wide text-slate-200">
                    ASTERPOS <span className="text-blue-400">RESTAURANT</span>
                  </div>
                </div>

                <Card className="border-slate-800/60 bg-slate-900/50 text-slate-100 shadow-[0_30px_80px_rgba(0,0,0,0.55)] backdrop-blur rounded-3xl">
                  <CardHeader className="space-y-2 pb-4">
                    <CardTitle className="text-2xl font-bold tracking-tight text-white">
                      Restaurant Back Office
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Secure access for <span className="text-slate-200">Owners</span>, <span className="text-slate-200">Managers</span> & <span className="text-slate-200">Staff</span>.
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* GOOGLE SIGN IN */}
                    <GoogleSignInButton 
                      onSuccess={async (credential) => {
                        try {
                          const response = await axios.post(`${API}/merchant/google-login`, { credential });
                          localStorage.setItem('backoffice_token', response.data.access_token);
                          setToken(response.data.access_token);
                          setManager(response.data.manager);
                          if (response.data.restaurants) {
                            setRestaurants(response.data.restaurants);
                          }
                          if (response.data.primary_restaurant_id) {
                            setActiveRestaurantId(response.data.primary_restaurant_id);
                            localStorage.setItem('backoffice_active_restaurant', response.data.primary_restaurant_id);
                          }
                          window.dispatchEvent(new Event('backoffice-login'));
                          toast.success('Welcome to Chain29 Back Office!');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Google login failed');
                        }
                      }}
                      onError={() => toast.error('Google sign-in failed')}
                      showOneTap={true}
                    />

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-slate-700" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-slate-900/50 px-2 text-slate-500">Or continue with email</span>
                      </div>
                    </div>

                    {/* PASSWORD LOGIN */}
                    <form onSubmit={handleLogin} className="space-y-5">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-300">Email</label>
                        <Input
                          type="email"
                          placeholder="you@restaurant.com"
                          value={loginForm.email}
                          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                          className="h-12 bg-slate-950/50 border-slate-700/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                          required
                          data-testid="login-email"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-300">Password</label>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••••"
                            value={loginForm.password}
                            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                            className="h-12 bg-slate-950/50 border-slate-700/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20 pr-12"
                            required
                            data-testid="login-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                          >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      {/* reCAPTCHA Enterprise v3 - invisible, handled in handleLogin */}

                      <Button
                        type="submit"
                        disabled={isLoggingIn}
                        className="w-full h-12 text-base font-semibold rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-[0_12px_30px_rgba(37,99,235,0.35)] transition-all"
                        data-testid="login-submit"
                      >
                        {isLoggingIn ? (
                          <span className="flex items-center gap-2">
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            Signing in...
                          </span>
                        ) : (
                          "Sign in to Back Office"
                        )}
                      </Button>
                    </form>

                    {/* No email-only "Sign in with OTP": the texted code is the second step
                        after the password (services/merchant_login_mfa) */}
                    {/* Links row */}
                    <div className="flex items-center justify-between text-sm pt-1">
                      <button
                        onClick={() => setShowPasswordReset(true)}
                        className="text-slate-400 hover:text-white underline underline-offset-4 transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>

                      <a
                        href="/system-status"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-500 hover:text-slate-200 underline underline-offset-4 transition-colors"
                        data-testid="system-status-link"
                      >
                        System Status
                      </a>
                    </div>

                    {/* Trust Footer */}
                    <div className="pt-2 text-xs text-slate-500 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-slate-500" />
                        Secure login • Role-based access
                      </span>
                      <span className="text-slate-600">v1.0</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mobile detection hook
  // (Moved to top of component with other hooks)
  
  // Sidebar navigation content - shared between desktop and mobile
  const SidebarContent = ({ onNavigate }) => (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-white/10 dark:border-[#1d3b53]">
        <div className="flex items-center gap-3">
          <img src={BACKOFFICE_LOGO} alt="Chain29" className="h-10 w-10" />
          <div>
            <span className="font-bold text-lg text-white dark:text-[#d6deeb]">
              Chain29
            </span>
            <p className="text-[10px] text-gray-400 dark:text-[#637777] -mt-1">Back Office</p>
          </div>
        </div>
      </div>

      {/* Location Switcher - Multi-Restaurant Support */}
      {restaurant && (
        <div className="px-4 py-3 border-b border-white/10 dark:border-[#1d3b53]">
          <button
            onClick={() => {
              setShowLocationSwitcher(true);
              onNavigate?.();
            }}
            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-white/10 dark:hover:bg-[#1d3b53]/50 transition-colors group"
            data-testid="location-switcher-btn"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-blue-600 dark:bg-[#82aaff] flex items-center justify-center flex-shrink-0">
                <Building2 className="h-4 w-4 text-white dark:text-[#011627]" />
              </div>
              <div className="text-left min-w-0">
                <p className="text-sm font-semibold text-white dark:text-[#d6deeb] truncate">
                  {restaurant.name || 'My Restaurant'}
                </p>
                <p className="text-xs text-gray-400 dark:text-[#637777] truncate">
                  {restaurants.length > 1 ? `${restaurants.length} locations` : 'Single location'}
                </p>
              </div>
            </div>
            {restaurants.length > 1 && (
              <ChevronRight className="h-4 w-4 text-gray-400 dark:text-[#637777] group-hover:text-white dark:group-hover:text-[#d6deeb] flex-shrink-0" />
            )}
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="p-4 space-y-1 overflow-y-auto flex-1">
        {(() => {
          // Role-aware filter for the sidebar.
          // - ChainAI group: staff can see the chat-style Command Center,
          //   but Operations Monitor / audit-log surfaces are
          //   manager-and-above.
          // - Menu & Inventory > AI Menu Import: manager-and-above only;
          //   staff don't need menu-import tooling.
          const role = (manager?.role || '').toLowerCase();
          const isManagerOrAbove =
            role === 'owner' || role === 'admin' || role === 'manager';
          const filteredNavGroups = navGroups
            .map((group) => {
              if (group.id === 'aster-ai') {
                const items = group.items.filter(
                  (item) => item.id !== 'ai-ops' || isManagerOrAbove,
                );
                if (items.length === 0) return null;
                return { ...group, items };
              }
              if (group.id === 'menu-inventory') {
                const items = group.items.filter(
                  (item) => item.id !== 'menu-import' || isManagerOrAbove,
                );
                if (items.length === 0) return null;
                return { ...group, items };
              }
              return group;
            })
            .filter(Boolean);
          return filteredNavGroups;
        })().map((group) => {
          const GroupIcon = group.icon;
          const isGroupExpanded = expandedGroups.includes(group.id);
          const hasActiveItem = group.items.some(item => isActivePath(item.path));
          
          // For groups without a label (like 'main'), render items directly
          if (!group.label) {
            return group.items.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(item.path);
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(item.path);
                    onNavigate?.();
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all min-h-[44px]",
                    isActive
                      ? "bg-blue-600 dark:bg-[#82aaff] text-white dark:text-[#011627]"
                      : "text-gray-300 dark:text-[#7fdbca] hover:bg-white/10 dark:hover:bg-[#1d3b53]/50 hover:text-white dark:hover:text-[#d6deeb]"
                  )}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            });
          }
          
          // Render collapsible group
          return (
            <div key={group.id} className="space-y-1">
              <button
                onClick={() => {
                  setExpandedGroups(prev => 
                    prev.includes(group.id) 
                      ? prev.filter(id => id !== group.id)
                      : [...prev, group.id]
                  );
                }}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-all",
                  hasActiveItem
                    ? "text-white dark:text-[#d6deeb] bg-white/5"
                    : "text-gray-400 dark:text-[#637777] hover:text-gray-200 dark:hover:text-[#7fdbca] hover:bg-white/5"
                )}
              >
                <div className="flex items-center gap-3">
                  <GroupIcon className="h-4 w-4" />
                  <span>{group.label}</span>
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform",
                  isGroupExpanded ? "rotate-180" : ""
                )} />
              </button>
              
              {isGroupExpanded && (
                <div className="ml-4 space-y-0.5 border-l border-white/10 dark:border-[#1d3b53] pl-3">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActivePath(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          navigate(item.path);
                          onNavigate?.();
                        }}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all min-h-[40px]",
                          isActive
                            ? "bg-blue-600 dark:bg-[#82aaff] text-white dark:text-[#011627] font-medium"
                            : "text-gray-300 dark:text-[#7fdbca] hover:bg-white/10 dark:hover:bg-[#1d3b53]/50 hover:text-white dark:hover:text-[#d6deeb]"
                        )}
                        data-testid={`nav-${item.id}`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Help & Logout */}
      <div className="p-4 border-t border-white/10 dark:border-[#1d3b53] mt-auto">
        <div className="space-y-1">
          <button className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-gray-300 dark:text-[#7fdbca] hover:bg-white/10 dark:hover:bg-[#1d3b53]/50 hover:text-white dark:hover:text-[#d6deeb] min-h-[44px]">
            <HelpCircle className="h-5 w-5" />
            <span>Help & Support</span>
          </button>
          <button
            onClick={() => {
              handleLogout();
              onNavigate?.();
            }}
            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-red-400 dark:text-[#ff5874] hover:bg-red-500/10 hover:text-red-300 dark:hover:text-[#ff5874] min-h-[44px]"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </>
  );
  
  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#011627] flex overflow-x-hidden" data-testid="backoffice-dashboard">
      {/* Mobile Sidebar Sheet */}
      <Sheet open={sidebarOpen && isMobile} onOpenChange={setSidebarOpen}>
        <SheetContent 
          side="left" 
          className="w-[280px] p-0 bg-[#1a1f37] dark:bg-[#011627] border-r-0 flex flex-col"
        >
          <SidebarContent onNavigate={() => setSidebarOpen(false)} />
        </SheetContent>
      </Sheet>
      
      {/* Desktop Sidebar - Fixed, always visible on lg+ */}
      <aside className="hidden lg:flex lg:flex-col fixed left-0 top-0 z-40 h-screen w-64 bg-[#1a1f37] dark:bg-[#011627]">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 transition-all duration-300 min-w-0">
        {/* Top Header - Responsive */}
        <header className="sticky top-0 z-20 h-14 sm:h-16 bg-white dark:bg-[#0b2942] border-b border-gray-200 dark:border-[#1d3b53] flex items-center justify-between px-3 sm:px-4 lg:px-8">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden flex-shrink-0 min-w-[44px] min-h-[44px]"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            {/* Page Title - Hidden on very small screens */}
            <h1 className="text-base sm:text-xl font-semibold text-gray-800 dark:text-[#d6deeb] truncate hidden xs:block">
              {restaurant?.name || 'Chain29'}
            </h1>
          </div>

          {/* Right Actions - Responsive spacing */}
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
            {/* Offline Sync Status */}
            <SyncDrawerTrigger className="hidden sm:inline-flex" />
            
            {/* Branch Switcher - Hidden on very small screens */}
            <div className="hidden sm:block">
              <BranchSwitcher variant="header" data-testid="global-branch-switcher" />
            </div>
            
            {/* Theme Toggle */}
            <ThemeToggle 
              variant="icon" 
              className="text-gray-500 dark:text-[#7fdbca] hover:text-gray-700 dark:hover:text-[#d6deeb] hover:bg-gray-100 dark:hover:bg-[#1d3b53] min-w-[44px] min-h-[44px]"
            />
            
            {/* Refresh Button - Hidden on mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="hidden sm:flex text-gray-500 dark:text-[#7fdbca] hover:text-gray-700 dark:hover:text-[#d6deeb] hover:bg-gray-100 dark:hover:bg-[#1d3b53] min-w-[44px] min-h-[44px]"
            >
              <RefreshCw className="h-5 w-5" />
            </Button>
            
            {/* Order Notifications with Sound Alerts */}
            <OrderNotifications 
              token={token} 
              onNewOrder={(order) => navigate(`${bo('orders')}?id=${order.id}`)}
            />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 hover:bg-gray-100 dark:hover:bg-[#1d3b53] rounded-lg min-h-[44px]">
                  <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-blue-600 dark:bg-[#82aaff] flex items-center justify-center text-white dark:text-[#011627] font-semibold text-sm flex-shrink-0">
                    {manager?.full_name?.charAt(0) || 'M'}
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400 dark:text-[#637777] hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 dark:bg-[#0b2942] dark:border-[#1d3b53]">
                <DropdownMenuLabel className="dark:text-[#d6deeb]">
                  <p className="font-medium">{manager?.full_name || 'Manager'}</p>
                  <p className="text-xs text-gray-500 dark:text-[#637777] font-normal truncate">{manager?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="dark:bg-[#1d3b53]" />
                <DropdownMenuItem onClick={() => navigate(bo('profile'))} className="dark:text-[#d6deeb] dark:hover:bg-[#1d3b53] dark:focus:bg-[#1d3b53] min-h-[44px]" data-testid="user-profile-link">
                  <User className="h-4 w-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(bo('settings'))} className="dark:text-[#d6deeb] dark:hover:bg-[#1d3b53] dark:focus:bg-[#1d3b53] min-h-[44px]">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-[#1d3b53]" />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-[#ff5874] dark:hover:bg-[#1d3b53] dark:focus:bg-[#1d3b53] min-h-[44px]">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content - Responsive padding */}
        <main className="p-3 sm:p-4 md:p-6 lg:p-8 bg-background min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)]">
          <BillingGate warning={billingWarning} />
          <PageErrorBoundary key={location.pathname}>
            <Outlet context={{
              token: token || '',
              manager: manager || { full_name: 'User', email: '', role: 'owner' },
              restaurant: restaurant || { name: 'Restaurant', id: activeRestaurantId },
              activeRestaurantId: activeRestaurantId || null,
              restaurants: restaurants || [],
            }} />
          </PageErrorBoundary>
        </main>
      </div>
      
      {/* Location Switcher Dialog */}
      <Dialog open={showLocationSwitcher} onOpenChange={setShowLocationSwitcher}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Switch Location
              </DialogTitle>
              <Button variant="outline" size="sm" onClick={() => navigate(bo('settings'))}>
                Manage
              </Button>
            </div>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative my-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name and address"
              className="pl-10"
              data-testid="location-search"
            />
          </div>
          
          {/* Multi-location groups */}
          {restaurants.length > 1 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-slate-500 mb-2">MULTI-LOCATION GROUPS</p>
              <button 
                onClick={() => {
                  // View all locations (no filter)
                  localStorage.removeItem('backoffice_active_restaurant');
                  setActiveRestaurantId(null);
                  setShowLocationSwitcher(false);
                  toast.success('Viewing all locations');
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 border border-slate-200 text-left"
              >
                <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">All Locations</p>
                  <p className="text-xs text-slate-500">{restaurants.length} locations</p>
                </div>
              </button>
            </div>
          )}
          
          {/* Individual Locations */}
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">LOCATIONS</p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {restaurants.map((loc) => (
                <button
                  key={loc.id}
                  onClick={() => handleSwitchLocation(loc.id)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left",
                    activeRestaurantId === loc.id
                      ? "bg-blue-50 border-2 border-blue-200"
                      : "hover:bg-slate-50 border border-slate-200"
                  )}
                  data-testid={`location-${loc.id}`}
                >
                  <div className={cn(
                    "h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0",
                    activeRestaurantId === loc.id
                      ? "bg-blue-600"
                      : "bg-slate-200"
                  )}>
                    <Store className={cn(
                      "h-5 w-5",
                      activeRestaurantId === loc.id ? "text-white" : "text-slate-600"
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-800 truncate">{loc.name}</p>
                      {loc.is_primary && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          Primary
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{loc.address || 'No address'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant={loc.is_open !== false ? "default" : "secondary"} className={cn(
                      "text-xs",
                      loc.is_open !== false ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                    )}>
                      {loc.is_open !== false ? 'Open' : 'Closed'}
                    </Badge>
                    {activeRestaurantId === loc.id && (
                      <Check className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {restaurants.length === 0 && (
            <div className="text-center py-6 text-slate-500">
              <Building2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No additional locations</p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowLocationSwitcher(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
